import React from "react";
import { act, fireEvent, render, screen, within } from "@testing-library/react";
import "@testing-library/jest-dom";
import IndependentQC, {
  ACCEPTED_FILE_TYPES,
  buildFindingsReport,
} from "./IndependentQC";
import { getFriendlyQCErrorMessage } from "../../utils/qcErrorMessages";
import {
  QCFinding,
  QCSectionProgress,
  QCSectionResult,
} from "../../types/qcTypes";

type StreamCallbacks = {
  onError: (error: string) => void;
  onReconnecting: (attempt: number, max: number) => void;
  onStarted: (total: number) => void;
  onSectionStart: (
    index: number,
    title: string,
    content: string,
    sectionId?: number,
  ) => void;
  onFinding: (finding: QCFinding) => void;
  onSectionComplete: (index: number, count: number) => void;
  onComplete: (total: number) => void;
  onSectionError: (
    index: number,
    count: number,
    error: string,
    section?: {
      sectionTitle?: string;
    },
  ) => void;
  onRecoveredViaPoll: (run: {
    findings: QCFinding[];
    sections?: QCSectionProgress[];
  }) => void;
};

const mockStartIndependentQC = jest.fn();
const mockConnectToStream = jest.fn();
const mockDisconnect = jest.fn();
const mockCancel = jest.fn();
const mockStop = jest.fn();
let mockStream: StreamCallbacks;
let mockReviewSections: QCSectionResult[] = [];

jest.mock("../../hooks/useQualityCheck", () => ({
  __esModule: true,
  default: () => ({
    startIndependentQC: mockStartIndependentQC,
    connectToStream: mockConnectToStream,
    disconnect: mockDisconnect,
    cancel: mockCancel,
    stop: mockStop,
    acceptFinding: jest.fn(),
    declineFinding: jest.fn(),
    revertFinding: jest.fn(),
    bulkResolve: jest.fn(),
    submitFeedback: jest.fn(),
    downloadTrackedDocument: jest.fn(),
    downloadLoading: false,
    loading: false,
  }),
}));
jest.mock("../../../../utils/useAuthToken", () => ({
  __esModule: true,
  default: () => async () => "token",
}));
jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));
// The Review step's markdown view (ESM react-markdown) isn't under test
// here; it records the sections it would show instead.
jest.mock("../../components/qc/IndependentQCDocumentView", () => ({
  __esModule: true,
  default: ({ sections }: { sections: QCSectionResult[] }) => {
    mockReviewSections = sections;
    return null;
  },
}));
// ESM-only packages the shared redline helpers load.
jest.mock("rehype-sanitize", () => ({ defaultSchema: { attributes: {} } }));
jest.mock("unist-util-visit-parents", () => ({
  SKIP: "skip",
  visitParents: jest.fn(),
}));

// Two drop areas: the document (single file) and the sources (multiple).
type DropHandler = (
  accepted: File[],
  rejections?: Array<{
    file: File;
    errors: { code: string; message: string }[];
  }>,
) => void;
const mockDrops: { document?: DropHandler; sources?: DropHandler } = {};
jest.mock("react-dropzone", () => ({
  useDropzone: ({
    onDrop,
    multiple,
  }: {
    onDrop: DropHandler;
    multiple: boolean;
  }) => {
    mockDrops[multiple ? "sources" : "document"] = onDrop;
    return {
      getRootProps: () => ({}),
      getInputProps: () => ({}),
      isDragActive: false,
    };
  },
}));

const doc = new File(["doc"], "report.md", { type: "text/markdown" });
const source = new File(["src"], "notes.txt", { type: "text/plain" });

const addFiles = () => {
  act(() => mockDrops.document!([doc]));
  act(() => mockDrops.sources!([source]));
};

// Lets the awaited start request and token lookup settle.
const settle = () =>
  act(() => new Promise((resolve) => setTimeout(resolve, 0)));

const startCheck = async () => {
  fireEvent.click(screen.getByRole("button", { name: "Start quality check" }));
  await settle();
};

const stepNamed = (label: string) => {
  const step = screen
    .getAllByRole("listitem")
    .find((item) => within(item).queryByText(label));
  if (!step) throw new Error(`No step labelled ${label}`);
  return step;
};

const finding = (over: Partial<QCFinding>): QCFinding => ({
  FindingID: 1,
  QCRunID: 7,
  SectionID: null,
  SectionTitle: "Results",
  SectionIndex: 0,
  OriginalText: "revenue grew 40%",
  SuggestedText: "revenue grew 14%",
  EditedText: null,
  StartOffset: 0,
  EndOffset: 16,
  FindingType: "inaccuracy",
  Severity: "critical",
  Explanation: "The annual report says 14%.",
  SourceDocumentID: null,
  SourceSnippet: "Revenue grew 14% year on year.",
  SourceName: "Annual report.pdf",
  Resolution: null,
  ResolvedDate: null,
  Helpful: null,
  ...over,
});

// Starts a check and streams one section with the given findings to the end.
const runToReview = async (findings: QCFinding[]) => {
  mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
  render(<IndependentQC />);
  addFiles();
  await startCheck();
  act(() => {
    mockStream.onStarted(2);
    mockStream.onSectionStart(0, "Results", "revenue grew 40%");
    findings.forEach((f) => mockStream.onFinding(f));
    mockStream.onSectionComplete(0, findings.length);
    mockStream.onSectionStart(1, "Outlook", "Stable.");
    mockStream.onSectionComplete(1, 0);
    mockStream.onComplete(findings.length);
  });
};

beforeEach(() => {
  jest.clearAllMocks();
  mockReviewSections = [];
  mockConnectToStream.mockImplementation(
    (_id: number, _token: string, _ws: string, callbacks: StreamCallbacks) => {
      mockStream = callbacks;
    },
  );
});

describe("IndependentQC step indicator", () => {
  it("marks the current step and numbers every step", () => {
    render(<IndependentQC />);

    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
    expect(stepNamed("Check")).not.toHaveAttribute("aria-current");
    // Numbered like the Document Type wizard.
    expect(within(stepNamed("Upload")).getByText("1")).toBeInTheDocument();
    expect(within(stepNamed("Check")).getByText("2")).toBeInTheDocument();
    expect(within(stepNamed("Review")).getByText("3")).toBeInTheDocument();
    // Labels are not buttons: they never did anything when clicked.
    expect(
      screen.queryByRole("button", { name: "Upload" }),
    ).not.toBeInTheDocument();
  });

  it("shows a check on finished steps", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();

    expect(stepNamed("Check")).toHaveAttribute("aria-current", "step");
    expect(
      within(stepNamed("Upload")).getByTestId("CheckIcon"),
    ).toBeInTheDocument();
  });
});

describe("IndependentQC upload", () => {
  it("says what's missing while Start is disabled", () => {
    render(<IndependentQC />);
    const start = screen.getByRole("button", { name: "Start quality check" });

    expect(start).toBeDisabled();
    expect(
      screen.getByText(
        "Add a document to check and at least one source to start.",
      ),
    ).toBeInTheDocument();

    addFiles();
    expect(start).toBeEnabled();
    expect(screen.queryByText(/to start\.$/)).not.toBeInTheDocument();
  });

  it("says which dropped file was refused and why", () => {
    render(<IndependentQC />);
    const sheet = new File(["x"], "figures.xlsx");

    act(() =>
      mockDrops.sources!(
        [],
        [
          {
            file: sheet,
            errors: [{ code: "file-invalid-type", message: "invalid" }],
          },
        ],
      ),
    );

    expect(screen.getByRole("alert")).toHaveTextContent(
      "figures.xlsx isn't a supported file. Use .pdf, .doc, .docx, .txt or .md.",
    );
  });

  it("accepts legacy Word files supported by the QC parser", () => {
    expect(ACCEPTED_FILE_TYPES["application/msword"]).toEqual([".doc"]);
    render(<IndependentQC />);
    const legacyWord = new File(["legacy"], "report.doc", {
      type: "application/msword",
    });

    act(() => mockDrops.document!([legacyWord]));

    expect(screen.queryByRole("alert")).not.toBeInTheDocument();
    expect(screen.getByText("report.doc")).toBeInTheDocument();
  });

  it("adds a source once, and removes the one asked for", () => {
    render(<IndependentQC />);
    const other = new File(["other"], "minutes.txt", { type: "text/plain" });

    act(() => mockDrops.sources!([source, other]));
    act(() => mockDrops.sources!([source]));
    expect(screen.getAllByText("notes.txt")).toHaveLength(1);

    fireEvent.click(screen.getByRole("button", { name: "Remove notes.txt" }));
    expect(screen.queryByText("notes.txt")).not.toBeInTheDocument();
    expect(screen.getByText("minutes.txt")).toBeInTheDocument();
  });
});

describe("IndependentQC failures", () => {
  it("says why the check could not start", async () => {
    mockStartIndependentQC.mockRejectedValue(new Error("Upload too large"));
    render(<IndependentQC />);
    addFiles();
    await startCheck();

    expect(
      screen.getByText(getFriendlyQCErrorMessage("Upload too large")),
    ).toBeInTheDocument();
    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
  });

  it("offers Retry and Start over when a running check fails", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    act(() => mockStream.onError("stream failed"));

    fireEvent.click(screen.getByRole("button", { name: "Retry" }));
    await settle();
    // The abandoned run is cancelled server-side, not just disconnected, so an
    // unfinished extraction stops holding the shared extraction slot.
    expect(mockCancel).toHaveBeenCalledTimes(1);
    // The same files are checked again.
    expect(mockStartIndependentQC).toHaveBeenCalledTimes(2);
    const form = mockStartIndependentQC.mock.calls[1][0] as FormData;
    expect((form.get("document_file") as File).name).toBe("report.md");

    act(() => mockStream.onError("stream failed again"));
    fireEvent.click(screen.getByRole("button", { name: "Start over" }));
    expect(mockCancel).toHaveBeenCalledTimes(2);
    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
    expect(screen.getByText("Document to check")).toBeInTheDocument();
  });

  it("clears what had streamed in when the run fails, showing the model's error", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    const message =
      "The AI model gpt-6-luna did not respond in time, so the quality check was stopped. " +
      "No results were kept. Please run the check again.";
    act(() => {
      mockStream.onStarted(2);
      mockStream.onSectionStart(0, "Results", "revenue grew 40%");
      mockStream.onFinding(finding({ FindingID: 1 }));
      mockStream.onSectionComplete(0, 1);
      mockStream.onSectionStart(1, "Methods", "n = 12");
      mockStream.onError(message);
    });

    expect(screen.getByText(message)).toBeInTheDocument();
    expect(screen.queryByText("Results")).toBeNull();
    expect(screen.queryByText("Methods")).toBeNull();
    expect(screen.queryByText(/1 finding/)).toBeNull();
    expect(screen.getByRole("button", { name: "Retry" })).toBeInTheDocument();
  });

  it("shows when the connection is being restored", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();

    expect(screen.getByText("Preparing…")).toBeInTheDocument();
    act(() => mockStream.onReconnecting(2, 5));
    expect(screen.getByText(/Reconnecting.*\(2\/5\)/)).toBeInTheDocument();

    act(() => mockStream.onStarted(3));
    expect(screen.queryByText(/Reconnecting/)).not.toBeInTheDocument();
    expect(screen.getByText("Checking sections… 0/3")).toBeInTheDocument();
  });

  it("uses the server's findings when a dropped stream is recovered", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    const first = finding({ FindingID: 1 });
    const second = finding({ FindingID: 2, OriginalText: "staff of 90" });
    act(() => {
      mockStream.onSectionStart(0, "Results", "revenue grew 40%");
      mockStream.onFinding(first);
      // The stream drops; polling finds the run finished with both findings.
      mockStream.onRecoveredViaPoll({ findings: [first, second] });
      mockStream.onComplete(2);
    });

    expect(mockReviewSections).toHaveLength(1);
    expect(mockReviewSections[0].findings.map((f) => f.FindingID)).toEqual([
      1, 2,
    ]);
    expect(mockReviewSections[0].status).toBe("complete");
  });
});

describe("IndependentQC with sections checked in parallel", () => {
  // The backend now checks several sections at once (#6272): several are
  // "checking" together, findings interleave, and sections finish out of
  // order. Each section's own events still arrive start -> findings -> done.
  it("tracks every section by index whatever order they finish in", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    const inSection0 = finding({ FindingID: 10, SectionIndex: 0 });
    const inSection2 = finding({
      FindingID: 12,
      SectionIndex: 2,
      SectionTitle: "Safety",
      OriginalText: "no effect at any dose",
    });

    act(() => {
      mockStream.onStarted(3);
      mockStream.onSectionStart(0, "Results", "revenue grew 40%");
      mockStream.onSectionStart(1, "Outlook", "Stable.");
      mockStream.onSectionStart(2, "Safety", "no effect at any dose");
    });
    expect(screen.getByText("Checking sections… 0/3")).toBeInTheDocument();

    // Section 2 finishes first, while 0 and 1 are still being checked.
    act(() => {
      mockStream.onFinding(inSection2);
      mockStream.onSectionComplete(2, 1);
    });
    expect(screen.getByText("Checking sections… 1/3")).toBeInTheDocument();
    expect(screen.getByText("1 finding so far")).toBeInTheDocument();

    act(() => {
      mockStream.onFinding(inSection0);
      mockStream.onSectionComplete(0, 1);
    });
    expect(screen.getByText("Checking sections… 2/3")).toBeInTheDocument();

    act(() => {
      mockStream.onSectionComplete(1, 0);
      mockStream.onComplete(2);
    });

    // Review gets every section, in document order, each with its own findings.
    expect(mockReviewSections.map((s) => s.sectionIndex)).toEqual([0, 1, 2]);
    expect(mockReviewSections.map((s) => s.status)).toEqual([
      "complete",
      "complete",
      "complete",
    ]);
    expect(
      mockReviewSections.map((s) => s.findings.map((f) => f.FindingID)),
    ).toEqual([[10], [], [12]]);
  });
});

describe("IndependentQC review", () => {
  it("counts only sections that were actually checked", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 7 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    act(() => {
      mockStream.onSectionStart(0, "Results", "text");
      mockStream.onFinding(finding({}));
      mockStream.onSectionComplete(0, 1);
      mockStream.onSectionStart(1, "Appendix", "table");
      mockStream.onSectionError(1, 0, "Section timed out");
      mockStream.onComplete(1);
    });

    const figure = (label: string) =>
      within(screen.getByRole("group", { name: label })).getByRole("heading")
        .textContent;
    expect(figure("Sections checked")).toBe("1");
    expect(figure("Findings")).toBe("1");
    expect(figure("Couldn't check")).toBe("1");
  });

  it("still counts a section with nothing checked as couldn't check", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 83 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    act(() => {
      mockStream.onSectionStart(0, "Results", "text");
      mockStream.onSectionError(0, 0, "Section timed out", {
        sectionTitle: "Results",
      });
      mockStream.onComplete(0);
    });

    const figure = (label: string) =>
      within(screen.getByRole("group", { name: label })).getByRole("heading")
        .textContent;
    expect(figure("Couldn't check")).toBe("1");
  });

  it("keeps a failed section failed when the run is recovered by polling", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 82 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    const progress = (
      index: number,
      over: Partial<QCSectionProgress>,
    ): QCSectionProgress => ({
      SectionIndex: index,
      SectionID: null,
      SectionTitle: `Section ${index}`,
      Status: "completed",
      FindingCount: 0,
      ErrorMessage: null,
      ...over,
    });
    act(() => {
      // The stream drops while both sections are still being checked: no
      // section_error or section_complete ever arrives.
      mockStream.onSectionStart(0, "Results", "text");
      mockStream.onSectionStart(1, "Annex", "short text");
      mockStream.onRecoveredViaPoll({
        findings: [],
        sections: [
          progress(0, {
            Status: "failed",
            ErrorMessage:
              "QC model returned invalid JSON for section 'Results'",
          }),
          progress(1, { Status: "completed" }),
        ],
      });
      mockStream.onComplete(0);
    });

    const figure = (label: string) =>
      within(screen.getByRole("group", { name: label })).getByRole("heading")
        .textContent;
    expect(figure("Couldn't check")).toBe("1");
    expect(figure("Sections checked")).toBe("1");
    expect(figure("Clean sections")).toBe("1");
  });

  it("Cancel check stops the running check and returns to the files", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 93 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    act(() => {
      mockStream.onSectionStart(0, "Results", "text");
    });

    fireEvent.click(screen.getByRole("button", { name: "Cancel check" }));

    expect(mockStop).toHaveBeenCalledTimes(1);
    expect(mockCancel).not.toHaveBeenCalled();
    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
  });

  it("does not offer Cancel check once the check has finished", async () => {
    await runToReview([finding({})]);

    expect(screen.queryByRole("button", { name: "Cancel check" })).toBeNull();
  });

  it("counts a section's findings once when a reconnect sends it again", async () => {
    mockStartIndependentQC.mockResolvedValue({ qc_run_id: 94 });
    render(<IndependentQC />);
    addFiles();
    await startCheck();
    act(() => {
      mockStream.onStarted(2);
      mockStream.onSectionStart(0, "Results", "text");
      mockStream.onFinding(finding({ FindingID: 1 }));
      mockStream.onFinding(finding({ FindingID: 2 }));
      // The stream drops; the reconnect follows the run and sends section 0 again.
      mockStream.onSectionStart(0, "Results", "text");
      mockStream.onFinding(finding({ FindingID: 1 }));
      mockStream.onFinding(finding({ FindingID: 2 }));
      mockStream.onSectionComplete(0, 2);
    });

    // While the run is still going (the server total arrives only at the end).
    expect(screen.getByText("2 findings so far")).toBeInTheDocument();
  });

  it("asks before discarding a review with unresolved findings", async () => {
    await runToReview([finding({})]);

    fireEvent.click(screen.getByRole("button", { name: "Start new check" }));
    const dialog = screen.getByRole("alertdialog", {
      name: "Discard this review?",
    });
    fireEvent.click(
      within(dialog).getByRole("button", { name: "Keep reviewing" }),
    );
    expect(stepNamed("Review")).toHaveAttribute("aria-current", "step");

    fireEvent.click(screen.getByRole("button", { name: "Start new check" }));
    fireEvent.click(
      screen.getByRole("button", { name: "Discard and start new check" }),
    );
    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
  });

  it("starts over straight away when nothing is left to resolve", async () => {
    await runToReview([finding({ Resolution: "accepted" })]);

    fireEvent.click(screen.getByRole("button", { name: "Start new check" }));
    expect(screen.queryByRole("alertdialog")).not.toBeInTheDocument();
    expect(stepNamed("Upload")).toHaveAttribute("aria-current", "step");
  });

  it("offers a findings report for any document type", async () => {
    const createObjectURL = jest.fn(() => "blob:report");
    Object.defineProperty(URL, "createObjectURL", {
      configurable: true,
      value: createObjectURL,
    });
    Object.defineProperty(URL, "revokeObjectURL", {
      configurable: true,
      value: jest.fn(),
    });
    const click = jest
      .spyOn(HTMLAnchorElement.prototype, "click")
      .mockImplementation(() => undefined);
    await runToReview([finding({})]);

    // report.md is not a Word file: no track changes, but a report.
    expect(
      screen.queryByRole("button", { name: "Download with track changes" }),
    ).not.toBeInTheDocument();
    fireEvent.click(
      screen.getByRole("button", { name: "Download findings report" }),
    );

    expect(createObjectURL).toHaveBeenCalledWith(expect.any(Blob));
    expect(click).toHaveBeenCalled();
    click.mockRestore();
  });
});

describe("buildFindingsReport", () => {
  it("lists every finding under its section with its outcome", () => {
    const report = buildFindingsReport(
      "report.md",
      [
        {
          sectionIndex: 0,
          sectionTitle: "Results",
          sectionContent: "",
          status: "complete",
          findings: [
            finding({ Resolution: "accepted", EditedText: "revenue grew 15%" }),
          ],
          findingCount: 1,
        },
        {
          sectionIndex: 1,
          sectionTitle: "Outlook",
          sectionContent: "",
          status: "complete",
          findings: [],
          findingCount: 0,
        },
        {
          sectionIndex: 2,
          sectionTitle: "Appendix",
          sectionContent: "",
          status: "error",
          findings: [],
          findingCount: 0,
        },
      ],
      new Date(2026, 9, 8, 12, 0),
    );

    expect(report).toContain("# Quality check findings: report.md");
    expect(report).toContain("1 finding in 3 sections.");
    expect(report).toContain("### 1. Critical · inaccuracy · Accepted");
    expect(report).toContain('- Original: "revenue grew 40%"');
    expect(report).toContain('- Your edit: "revenue grew 15%"');
    expect(report).toContain(
      '- Source: Annual report.pdf ("Revenue grew 14% year on year.")',
    );
    expect(report).toMatch(/## Outlook\n\nNo issues found\./);
    expect(report).toMatch(
      /## Appendix\n\nThis section could not be checked\./,
    );
  });
});
